import { ReactNode } from "react";

const PLANNER_BUSY_MESSAGE =
  "В момента се изчислява друг график. Моля изчакайте го да приключи и опитайте отново.";

const TEMPORARY_ERROR_MESSAGE =
  "Временна грешка на сървъра. Моля опитайте отново по-късно.";

const REASON_MESSAGES: Record<string, string> = {
  planner_already_running: PLANNER_BUSY_MESSAGE,
};

// Съобщение според HTTP кода на отговора от услугата за изчисляване
function httpStatusMessage(httpStatus: number): string | undefined {
  if (httpStatus === 503) {
    return PLANNER_BUSY_MESSAGE;
  } else if (httpStatus >= 500 && httpStatus < 600) {
    return TEMPORARY_ERROR_MESSAGE;
  } else if (httpStatus >= 400 && httpStatus < 500) {
    return "Грешка на сървъра. Моля свържете се с администратор.";
  }
  return undefined;
}

// Съобщенията от отказ на команда по websocket канала (callAsyncFunction).
// Сървърът връща { status: "error", ... } с едно от:
//   - reason - код на причината (напр. planner_already_running), а при
//     planner_start_error и http_code - HTTP кодът от услугата за изчисляване;
//   - errors - списък от { message } - грешки при изпълнение на командата;
//   - errors - обект { поле: [съобщения] } - невалидни параметри (Ecto
//     changeset);
//   - errors - празен списък - няма права или командата е непозната.
// Без отговор (изтекло време) или без канал се получава Error/низ.
export function serverErrorMessages(error: unknown): string[] {
  if (error instanceof Error) {
    return error.message === "timeout"
      ? ["Няма отговор от сървъра"]
      : [error.message];
  }
  if (error === "Channel closed") {
    return ["Няма връзка със сървъра"];
  }
  if (typeof error !== "object" || error === null) {
    return [];
  }

  const reason = (error as { reason?: unknown }).reason;
  if (reason === "planner_start_error") {
    // Отказ от услугата за изчисляване - http_code е null, ако тя изобщо не е
    // отговорила
    const httpCode = (error as { http_code?: unknown }).http_code;
    return [
      (typeof httpCode === "number" && httpStatusMessage(httpCode)) ||
        TEMPORARY_ERROR_MESSAGE,
    ];
  }
  if (typeof reason === "string" && reason.length > 0) {
    // Непознатите причини се показват както са - за диагностика
    return [REASON_MESSAGES[reason] ?? reason];
  }

  const errors = (error as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors
      .map((e) => (e as { message?: unknown })?.message)
      .filter((m): m is string => typeof m === "string" && m.length > 0);
  }
  if (typeof errors === "object" && errors !== null) {
    return Object.entries(errors).flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages])
        .filter((m): m is string => typeof m === "string")
        .map((m) => `${field}: ${m}`)
    );
  }
  return [];
}

export function serverErrorContent(title: string, error: unknown): ReactNode {
  const messages = serverErrorMessages(error);
  return messages.length > 0 ? (
    <>
      <div>{title}:</div>
      <ul>
        {messages.map((message, idx) => (
          <li key={idx}>{message}</li>
        ))}
      </ul>
    </>
  ) : (
    title
  );
}
